import {
  PublicClientApplication,
  type Configuration,
  type AccountInfo,
  type SilentRequest,
} from "@azure/msal-browser"

// Pulled from terraform output `entra_app_client_id` — Mission Control SPA
const clientId = import.meta.env.VITE_MC_CLIENT_ID || "401c4e92-a1ef-4a4a-b909-9a4d94130146"
const tenantId = import.meta.env.VITE_MC_TENANT_ID || "ede1d47c-7af2-47d7-835e-2c48475ab410"

export const apiScope = `api://${clientId}/user_impersonation`

const config: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin + "/auth/callback",
    postLogoutRedirectUri: window.location.origin + "/",
    navigateToLoginRequestUrl: false, // we own the post-login redirect via the router
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    // Verbose logging — visible in browser console while we stabilise sign-in.
    loggerOptions: {
      loggerCallback: (level, message) => {
        if (level <= 2) console.log("[msal]", message)
      },
      logLevel: 2, // 0=Error, 1=Warning, 2=Info, 3=Verbose
      piiLoggingEnabled: false,
    },
  },
}

export const msalInstance = new PublicClientApplication(config)

export const loginRequest = {
  scopes: ["openid", "profile", "User.Read", apiScope],
  // Always show the account picker so users with multiple Microsoft accounts
  // (e.g. work + personal) explicitly choose which one to sign in with.
  // Without this, MSAL silently uses whichever account is "active" in the
  // browser session, which led to a guest-identity token instead of the
  // native cyntegrity.com account during early testing.
  prompt: "select_account",
  // Hint MSAL to prefer the native cyntegrity.com domain in the picker.
  domainHint: "cyntegrity.com",
}

export function activeAccount(): AccountInfo | null {
  const all = msalInstance.getAllAccounts()
  if (all.length === 0) return null
  return msalInstance.getActiveAccount() ?? all[0]
}

export async function acquireApiToken(opts: { forceRefresh?: boolean } = {}): Promise<string> {
  const account = activeAccount()
  if (!account) throw new Error("Not signed in")

  // Default: use MSAL's cache (saves a network hop). Pass `forceRefresh: true`
  // explicitly when you need fresh claims (e.g. after a role-assignment change).
  // The dashboard's "Refresh token" button is the natural escape hatch.
  const req: SilentRequest = {
    account,
    scopes: [apiScope],
    forceRefresh: opts.forceRefresh ?? false,
  }
  try {
    const r = await msalInstance.acquireTokenSilent(req)
    return r.accessToken
  } catch {
    const r = await msalInstance.acquireTokenPopup({ scopes: [apiScope] })
    return r.accessToken
  }
}
